// Trail cards: sources, groups of steps, and outputs.
import { Handle, Position } from "@xyflow/react";
import { Tooltip } from "antd";
import { describeStep, getStepDefinition } from "../../../../engine";
import { useDownload } from "../../data/useDownload";
import { formatCount } from "../../shared/display";
import { useBuilder } from "../BuilderContext";
import { DeleteOutput, DeleteSource, DeleteStep, SHOW_ON_CARD_HOVER, SHOW_ON_ROW_HOVER } from "../DeleteControls";
import { ColorPicker, DEFAULT_COLORS, tint } from "./colors";

const DOT = { ok: "bg-teal-600", warning: "bg-amber-500", error: "bg-red-500", idle: "bg-gray-300" };

const Dot = ({ status, title }) => (
  <Tooltip title={title}>
    <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${DOT[status]}`} />
  </Tooltip>
);

const Badge = ({ children, color }) => (
  <span
    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-mono text-[10px] font-semibold"
    style={{ background: tint(color), color }}
  >
    {children}
  </span>
);

const SmallButton = ({ children, onClick, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={(event) => {
      event.stopPropagation();
      onClick();
    }}
    className="nodrag nopan rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-800 hover:border-teal-600 hover:text-teal-700 disabled:cursor-wait disabled:opacity-60"
  >
    {children}
  </button>
);

const Card = ({ selected, children, onClick, color, width = 300 }) => (
  <div
    onClick={onClick}
    style={{ width, borderTop: `3px solid ${color}` }}
    className={`group/card pointer-events-auto cursor-grab active:cursor-grabbing rounded-xl border bg-white shadow-sm transition hover:shadow-md ${
      selected ? "border-teal-700 ring-2 ring-teal-700/20" : "border-gray-200"
    }`}
  >
    {children}
  </div>
);

const Footer = ({ children }) => <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-3 py-2 font-mono text-xs font-medium text-gray-700">{children}</div>;

const fileBadge = (name, format) => (format === "csv" || /\.(csv|txt)$/i.test(name || "") ? "CSV" : "XLS");

export const SourceNode = ({ data: { source, color } }) => {
  const { state, run, select, openNode, dispatch } = useBuilder();
  const table = run?.tables[source.id];
  const hasSample = Boolean(state.files[source.id]);
  const issues = run?.sourceIssues[source.id] || [];
  const status = !hasSample ? "idle" : issues.some((i) => i.level === "error") ? "error" : issues.length ? "warning" : "ok";
  return (
    <Card color={color} selected={state.selectedId === source.id} onClick={() => select(source.id)}>
      <div className="flex gap-3 p-3">
        <Badge color={color}>{fileBadge(source.originalName)}</Badge>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-semibold text-gray-900">{source.label}</span>
            <span className="flex items-center gap-1">
              <ColorPicker
                title="Card colour"
                value={source.color}
                defaultColor={DEFAULT_COLORS.source}
                className={SHOW_ON_CARD_HOVER}
                onChange={(next) => dispatch({ type: "updateSource", id: source.id, changes: { color: next } })}
              />
              <DeleteSource source={source} className={SHOW_ON_CARD_HOVER} />
              <Dot status={status} title={hasSample ? "Sample loaded" : "No sample file"} />
            </span>
          </div>
          <div className="truncate font-mono text-xs text-gray-600">
            {source.id.replace("source", "Source ")} · {source.originalName}
          </div>
          {!hasSample && <div className="mt-1 text-[11px] text-amber-700">Upload a sample file to preview</div>}
          {issues.slice(0, 2).map((issue) => (
            <div key={issue.message} className={`mt-1 text-[11px] line-clamp-2 ${issue.level === "error" ? "text-red-600" : "text-amber-700"}`}>
              {issue.message}
            </div>
          ))}
        </div>
      </div>
      <Footer>
        <span>
          {hasSample ? `${formatCount(table?.rowCount)} rows · ` : ""}
          {source.columns.length} cols
        </span>
        <SmallButton onClick={() => openNode(source.id)}>Preview</SmallButton>
      </Footer>
      <Handle type="source" position={Position.Right} className="!h-2.5 !w-2.5 !border-2 !border-white !bg-gray-400" />
    </Card>
  );
};

const stepStatus = (run, step) => {
  const result = run?.results.find((r) => r.stepId === step.id);
  if (run?.error?.stepId === step.id) return ["error", result?.error];
  if (!result) return ["idle", "Not run yet"];
  if (result.warnings?.length) return ["warning", result.warnings.join(" ")];
  return ["ok", "OK"];
};

export const StepsNode = ({ data: { group, color } }) => {
  const { state, run, select, setTalkingAbout, dispatch } = useBuilder(); // openNode was used by Preview
  const first = group.steps[0];
  const last = group.steps[group.steps.length - 1];
  const statuses = group.steps.map(({ step }) => stepStatus(run, step)[0]);
  const warnings = statuses.filter((s) => s === "warning").length;
  const errors = statuses.filter((s) => s === "error").length;
  const output = run?.tables[last.step.id];
  // Preview (switched off for now) opened the selected step of this card, else its last step.
  // const previewId = group.steps.some(({ step }) => step.id === state.selectedId) ? state.selectedId : last.step.id;
  const title = group.steps.length > 1 ? `Steps ${first.index + 1}–${last.index + 1}` : first.step.label || getStepDefinition(first.step.type)?.label;

  return (
    <Card width={340} color={color} selected={group.steps.some(({ step }) => step.id === state.selectedId)}>
      <Handle type="target" position={Position.Left} className="!h-2.5 !w-2.5 !border-2 !border-white !bg-gray-400" />
      <div className="flex items-center gap-2 px-3 pb-1.5 pt-3">
        <span className="truncate text-sm font-semibold text-gray-900">{title}</span>
        {group.steps.length > 1 && <span className="rounded bg-gray-100 px-1.5 text-[10px] text-gray-600">{group.steps.length} steps</span>}
        {warnings > 0 && <span className="rounded bg-amber-50 px-1.5 text-[10px] text-amber-800">{warnings} warning{warnings > 1 ? "s" : ""}</span>}
        {errors > 0 && <span className="rounded bg-red-50 px-1.5 text-[10px] text-red-700">error</span>}
        <ColorPicker
          title="Card colour"
          value={first.step.color}
          defaultColor={DEFAULT_COLORS.steps}
          className={`ml-auto -my-1 ${SHOW_ON_CARD_HOVER}`}
          onChange={(next) => dispatch({ type: "updateStep", id: first.step.id, changes: { color: next } })}
        />
      </div>
      <div className="px-1.5 pb-1.5">
        {group.steps.map(({ step, index }) => {
          const definition = getStepDefinition(step.type);
          const result = run?.results.find((r) => r.stepId === step.id);
          const [status, statusTitle] = stepStatus(run, step);
          return (
            <div
              key={step.id}
              onClick={() => select(step.id)}
              className={`group/row flex items-start gap-2 rounded-md px-2 py-1.5 ${state.selectedId === step.id ? "bg-teal-50" : "hover:bg-gray-50"} ${step.disabled ? "opacity-50" : ""}`}
            >
              <span className="w-4 shrink-0 pt-px font-mono text-xs font-medium text-gray-500">{index + 1}</span>
              <div className="min-w-0 flex-1 text-[13px] leading-snug">
                <span className="font-semibold text-gray-900">{step.label || definition?.label}</span>{" "}
                <span className={status === "error" ? "text-red-600" : "text-gray-600"}>{status === "error" ? statusTitle : describeStep(step)}</span>
              </div>
              <span className="shrink-0 pt-px font-mono text-xs font-medium text-gray-700">{result ? `${formatCount(result.rowsIn)} → ${formatCount(result.rowsOut)}` : "–"}</span>
              <span className="pt-1">
                <Dot status={status} title={statusTitle} />
              </span>
              <DeleteStep step={step} className={`-my-0.5 ${SHOW_ON_ROW_HOVER}`} />
            </div>
          );
        })}
      </div>
      <Footer>
        <span>
          {output ? `${output.rowCount.toLocaleString()} rows · ${output.columns.length} cols` : "not run"}
          {errors > 0 && <span className="text-red-600"> · {errors} err</span>}
        </span>
        <span className="flex gap-1">
          <SmallButton
            onClick={() => {
              select(last.step.id);
              setTalkingAbout(last.step.id);
            }}
          >
            Ask AI
          </SmallButton>
          {/* Removed for now: the output card already has Preview.
          <SmallButton onClick={() => openNode(previewId)}>Preview</SmallButton> */}
        </span>
      </Footer>
      <Handle type="source" position={Position.Right} className="!h-2.5 !w-2.5 !border-2 !border-white !bg-gray-400" />
    </Card>
  );
};

export const OutputNode = ({ data: { output, color } }) => {
  const { state, outputs, select, openNode, dispatch } = useBuilder();
  const file = outputs.files.find((f) => f.id === output.id);
  const error = outputs.errorsById?.[output.id]?.[0];
  const sheet = file?.sheets[0];
  const rows = sheet?.rowCount ?? 0;
  const cols = sheet?.columnCount ?? 0;
  const [downloading, download] = useDownload();
  return (
    <Card color={color} selected={state.selectedId === output.id} onClick={() => select(output.id)}>
      <Handle type="target" position={Position.Left} className="!h-2.5 !w-2.5 !border-2 !border-white !bg-teal-600" />
      <div className="flex gap-3 p-3">
        <Badge color={color}>OUT</Badge>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-gray-900">Output</span>
            <span className="flex items-center gap-1">
              <ColorPicker
                title="Card colour"
                value={output.color}
                defaultColor={DEFAULT_COLORS.outputFile}
                className={SHOW_ON_CARD_HOVER}
                onChange={(next) => dispatch({ type: "updateOutput", id: output.id, changes: { color: next } })}
              />
              <DeleteOutput output={output} className={SHOW_ON_CARD_HOVER} />
              <span className="font-mono text-[10px] text-gray-400">{output.format === "xlsx" ? "Excel" : "CSV"}</span>
            </span>
          </div>
          <div className="truncate font-mono text-xs text-gray-600">{output.fileName}.{output.format}</div>
          {error && <div className="mt-1 text-[11px] text-red-600 line-clamp-2">{error}</div>}
          {file && file.sheets.length > 1 && <div className="mt-1 text-[11px] text-gray-500">{file.sheets.length} sheets</div>}
        </div>
      </div>
      <Footer>
        <span>{file ? `${rows.toLocaleString()} rows · ${cols} cols` : "not ready"}</span>
        <span className="flex gap-1">
          {file && (
            <SmallButton disabled={downloading} onClick={() => download("editor", output)}>
              {downloading ? "Preparing…" : "Download"}
            </SmallButton>
          )}
          <SmallButton onClick={() => openNode(output.id)}>Preview</SmallButton>
        </span>
      </Footer>
    </Card>
  );
};

// "outputFile", not "output": React Flow styles its built-in "output" type with a box.
export const nodeTypes = { source: SourceNode, steps: StepsNode, outputFile: OutputNode };
